// Preset do PrimeNG aplicado por `providePrimeNG` em `app.config.ts`.
//
// ObraSync 3.0: o PrimeNG não tem cores próprias. Os papéis (ação, texto,
// superfície, borda, campo) apontam para os tokens `--os-*` de
// `src/styles/_tokens.scss`, então claro e escuro trocam junto com o resto do
// app, sem uma segunda tabela de cores para manter.
//
// Só ficam escalas cruas onde o PrimeNG faz conta com elas (`{primary.100}`,
// `{surface.300}`…). O PrimeNG fica atrás dos componentes `os-*` de
// `shared/ui`; tela nenhuma deveria precisar dele direto.
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

/** Terracota em degraus; o 500 é o `--os-accent` do claro. */
const TERRACOTA = {
	50: '#fbf1ed',
	100: '#f8e6de',
	200: '#efc6b5',
	300: '#e3a088',
	400: '#d47657',
	500: '#bf4a26',
	600: '#a94020',
	700: '#9c381c',
	800: '#7a2c16',
	900: '#5a2011',
	950: '#34120a',
};

/** Papéis comuns aos dois esquemas: tudo lê os tokens do app. */
const ROLES = {
	primary: {
		color: 'var(--os-accent)',
		contrastColor: 'var(--os-on-accent)',
		hoverColor: 'var(--os-accent-strong)',
		activeColor: 'var(--os-accent-strong)',
	},
	highlight: {
		background: 'var(--os-accent-soft)',
		focusBackground: 'var(--os-accent-soft)',
		color: 'var(--os-accent-strong)',
		focusColor: 'var(--os-accent-strong)',
	},
	mask: {
		background: 'var(--os-scrim)',
		color: 'var(--os-ink)',
	},
	formField: {
		background: 'var(--os-surface)',
		disabledBackground: 'var(--os-surface-sunken)',
		filledBackground: 'var(--os-surface-sunken)',
		filledHoverBackground: 'var(--os-surface-sunken)',
		filledFocusBackground: 'var(--os-surface)',
		borderColor: 'var(--os-border-strong)',
		hoverBorderColor: 'var(--os-ink-muted)',
		focusBorderColor: 'var(--os-accent)',
		invalidBorderColor: 'var(--os-risk)',
		color: 'var(--os-ink)',
		disabledColor: 'var(--os-ink-muted)',
		placeholderColor: 'var(--os-ink-muted)',
		invalidPlaceholderColor: 'var(--os-risk)',
		floatLabelColor: 'var(--os-ink-muted)',
		floatLabelFocusColor: 'var(--os-accent-strong)',
		floatLabelActiveColor: 'var(--os-ink-muted)',
		floatLabelInvalidColor: 'var(--os-risk)',
		iconColor: 'var(--os-ink-muted)',
		shadow: 'none',
	},
	text: {
		color: 'var(--os-ink-soft)',
		hoverColor: 'var(--os-ink)',
		mutedColor: 'var(--os-ink-muted)',
		hoverMutedColor: 'var(--os-ink-soft)',
	},
	content: {
		background: 'var(--os-surface)',
		hoverBackground: 'var(--os-surface-sunken)',
		borderColor: 'var(--os-border)',
		color: 'var(--os-ink-soft)',
		hoverColor: 'var(--os-ink)',
	},
	overlay: {
		select: { background: 'var(--os-surface)', borderColor: 'var(--os-border)', color: 'var(--os-ink-soft)' },
		popover: { background: 'var(--os-surface)', borderColor: 'var(--os-border)', color: 'var(--os-ink-soft)' },
		modal: { background: 'var(--os-surface)', borderColor: 'var(--os-border)', color: 'var(--os-ink-soft)' },
	},
	list: {
		option: {
			focusBackground: 'var(--os-surface-sunken)',
			selectedBackground: 'var(--os-accent-soft)',
			selectedFocusBackground: 'var(--os-accent-soft)',
			color: 'var(--os-ink-soft)',
			focusColor: 'var(--os-ink)',
			selectedColor: 'var(--os-accent-strong)',
			selectedFocusColor: 'var(--os-accent-strong)',
			icon: { color: 'var(--os-ink-muted)', focusColor: 'var(--os-ink)' },
		},
		optionGroup: { background: 'transparent', color: 'var(--os-ink-muted)' },
	},
	navigation: {
		item: {
			focusBackground: 'var(--os-surface-sunken)',
			activeBackground: 'var(--os-surface-sunken)',
			color: 'var(--os-ink-soft)',
			focusColor: 'var(--os-ink)',
			activeColor: 'var(--os-ink)',
			icon: { color: 'var(--os-ink-muted)', focusColor: 'var(--os-ink)', activeColor: 'var(--os-ink)' },
		},
		submenuLabel: { background: 'transparent', color: 'var(--os-ink-muted)' },
		submenuIcon: { color: 'var(--os-ink-muted)', focusColor: 'var(--os-ink)', activeColor: 'var(--os-ink)' },
	},
};

export const PrimeNgTheme = definePreset(Aura, {
	primitive: {
		borderRadius: {
			none: '0',
			xs: '4px',
			sm: '6px',
			md: '8px',
			lg: '12px',
			xl: '20px',
		},
	},
	semantic: {
		transitionDuration: '0.18s',
		focusRing: {
			width: '2px',
			style: 'solid',
			color: 'var(--os-accent)',
			offset: '2px',
			shadow: 'none',
		},
		disabledOpacity: '0.5',
		iconSize: '1rem',
		anchorGutter: '4px',
		primary: TERRACOTA,
		formField: {
			paddingX: '0.75rem',
			paddingY: '0.5625rem',
			sm: { fontSize: '0.8125rem', paddingX: '0.625rem', paddingY: '0.375rem' },
			lg: { fontSize: '1rem', paddingX: '0.875rem', paddingY: '0.75rem' },
			borderRadius: '{border.radius.md}',
			// Foco como no protótipo: borda terracota e um halo suave.
			focusRing: {
				width: '0',
				style: 'none',
				color: 'transparent',
				offset: '0',
				shadow: '0 0 0 3px var(--os-accent-soft)',
			},
			transitionDuration: '{transition.duration}',
		},
		list: {
			padding: '0.25rem',
			gap: '2px',
			header: { padding: '0.5rem 0.75rem 0.25rem' },
			option: { padding: '0.5rem 0.75rem', borderRadius: '{border.radius.md}' },
			optionGroup: { padding: '0.5rem 0.75rem', fontWeight: '700' },
		},
		content: { borderRadius: '{border.radius.lg}' },
		mask: { transitionDuration: '0.18s' },
		navigation: {
			list: { padding: '0.25rem', gap: '2px' },
			item: { padding: '0.5rem 0.75rem', borderRadius: '{border.radius.md}', gap: '0.625rem' },
			submenuLabel: { padding: '0.5rem 0.75rem', fontWeight: '700' },
			submenuIcon: { size: '0.875rem' },
		},
		overlay: {
			select: { borderRadius: '{border.radius.lg}', shadow: 'var(--os-shadow-overlay)' },
			popover: { borderRadius: '{border.radius.lg}', padding: '0.75rem', shadow: 'var(--os-shadow-overlay)' },
			modal: { borderRadius: '{border.radius.xl}', padding: '1.5rem', shadow: 'var(--os-shadow-overlay)' },
			navigation: { shadow: 'var(--os-shadow-overlay)' },
		},
		colorScheme: {
			light: {
				surface: {
					0: '#ffffff',
					50: '#fbfaf8',
					100: '#f4f2ee',
					200: '#e9e6df',
					300: '#d9d4ca',
					400: '#b3ab9e',
					500: '#8b8377',
					600: '#6f685d',
					700: '#4b463d',
					800: '#33302a',
					900: '#23211d',
					950: '#1c1b19',
				},
				...ROLES,
			},
			dark: {
				surface: {
					0: '#ffffff',
					50: '#f4f2ee',
					100: '#e6e2db',
					200: '#d6d2cb',
					300: '#b8b2a8',
					400: '#9c968c',
					500: '#6f6a63',
					600: '#504c47',
					700: '#46433f',
					800: '#33312e',
					900: '#1c1b1a',
					950: '#141312',
				},
				...ROLES,
			},
		},
	},
});
